import { createSlice } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { normalizeMessage, normalizeMessages } from "@/utils/common";

const getInitialHistoryInfo = () => ({ message: null, scrollTopCount: null });

const initialState = {
    messages: {},
    receiveMessage: [],
    notifyMessage: {},
    sendMsg: null,
    historyMessagesInfo: getInitialHistoryInfo(),
};

const persistConfig = {
    key: "messages",
    storage,
    version: 2,
    whitelist: ["receiveMessage", "notifyMessage"], // message arrays are cached in IndexedDB instead
    migrate: (state) => Promise.resolve(state ? {
        ...state,
        messages: {},
        sendMsg: null,
        historyMessagesInfo: getInitialHistoryInfo(),
    } : state),
};

const hasValue = (value) => value !== undefined && value !== null && value !== "";

const isSameMessage = (current, next) => {
    if (hasValue(next.local_id) && current.local_id === next.local_id) return true;
    if (hasValue(next.id) && current.id === next.id) return true;
    return false;
};

const mergeRoomMessages = (currentMessages, incomingMessages) => {
    const roomMessages = [...(currentMessages || [])];

    for (const message of incomingMessages) {
        const messageIndex = roomMessages.findIndex((item) => isSameMessage(item, message));

        if (messageIndex === -1) {
            roomMessages.push(message);
        } else {
            roomMessages[messageIndex] = {
                ...roomMessages[messageIndex],
                ...message,
            };
        }
    }

    return roomMessages;
};

const messagesSlice = createSlice({
    name: "messages",
    initialState,
    reducers: {
        insertMessages: (state, action) => {
            const messages = normalizeMessages(action.payload);
            if (!messages.length) return;

            const roomId = messages[0].room_id;

            if(!state.messages[roomId]) return;

            state.messages[roomId] = mergeRoomMessages(state.messages[roomId], messages);
        },
        updateMessages: (state, action) => {
            const messages = normalizeMessages(action.payload);
            if (!messages.length) return;

            const roomId = messages[0].room_id;

            if(!state.messages[roomId]) return;

            state.messages[roomId] = mergeRoomMessages(state.messages[roomId], messages);
        },
        deleteMessages: (state, action) => {
            const messageIds = action.payload || [];
            if (!messageIds.length) return;

            let firstMessage = null;

            for (const messages of Object.values(state.messages)) {
                firstMessage = messages.find((message) => message.id === messageIds[0]);
                if (firstMessage) break;
            }

            if (!firstMessage) return;

            const roomId = firstMessage.room_id;
            state.messages[roomId] = (state.messages[roomId] || []).filter(
                (message) => !messageIds.includes(message.id)
            );
        },
        updateHistoryMessages: (state, action) => {
            const messagesInfo = action.payload || {};
            if (!messagesInfo.room_id) return;

            const prevMessages = state.messages[messagesInfo.room_id] || [];
            const newMessages = normalizeMessages(messagesInfo.messages);
            const mergedMessages = [...prevMessages, ...newMessages].filter(
                (message, index, self) => index === self.findIndex((item) => item.id === message.id)
            );
            const scrollTopCount = Math.ceil((newMessages.length - 30) / 10);

            state.messages[messagesInfo.room_id] = mergedMessages;
            state.historyMessagesInfo = { message: normalizeMessage(messagesInfo.message), scrollTopCount };
        },
        setRoomMessages: (state, action) => {
            const { room_id, messages } = action.payload || {};
            if (!room_id) return;

            state.messages[room_id] = normalizeMessages(messages);
        },
        clearHistoryMessagesInfo: (state) => {
            state.historyMessagesInfo = getInitialHistoryInfo();
        },
        setSendMsg: (state, action) => {
            state.sendMsg = action.payload;
        },
        clearRoomMessages: (state, action) => {
            state.messages[action.payload] = [];
        },
        receiveMessages: (state) => {
            state.receiveMessage.push(1);
        },
        audioMessages: (state) => {
            state.receiveMessage = state.receiveMessage.slice(0, -1);
        },
        notifyMessages: (state, action) => {
            state.notifyMessage = normalizeMessage(action.payload) || {};
        },
        closeNotifyMessages: (state) => {
            state.notifyMessage = {};
        },
        closeAllMessages: (state) => {
            state.messages = {};
        },
    },
});

export const {
    insertMessages,
    updateMessages,
    deleteMessages,
    updateHistoryMessages,
    setRoomMessages,
    clearHistoryMessagesInfo,
    setSendMsg,
    clearRoomMessages,
    receiveMessages,
    audioMessages,
    notifyMessages,
    closeNotifyMessages,
    closeAllMessages,
} = messagesSlice.actions;

export default persistReducer(persistConfig, messagesSlice.reducer);
